import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Plus, User, LogOut, MessageSquare, Shield, Bell, Heart, ShieldCheck, Store, HelpCircle, BookmarkCheck, Bookmark, BarChart3, Lock, FileText, Zap, Briefcase, UserPlus, Tag, ChevronDown, Home } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import NotificationsBell from "./NotificationsBell";
import LanguagePicker from "./LanguagePicker";
import VerifiedBadge from "./VerifiedBadge";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

// v79.48 — Founder direction: 'Post Add "+" when clicked users are not
// seeing options to post job opening, create my job profile options.'
// The header "+" button now opens a dropdown menu with 4 posting flows
// instead of dumping the user straight onto the generic Post Ad form.
// Unauthenticated users are routed via `/register?next=…` so the intent
// is preserved through the auth wall.
function PostMenu({ compact = false, testIdSuffix = "" }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const go = (path) => {
        navigate(user ? path : `/register?next=${encodeURIComponent(path)}`);
    };
    const items = [
        { key: "ad",       Icon: Zap,       label: "List an Ad",             sub: "Sell anything — products, services, gigs", to: "/post" },
        { key: "job",      Icon: Briefcase, label: "Post a Job Opening",     sub: "Hire candidates worldwide",               to: "/post?category=jobs" },
        { key: "profile",  Icon: UserPlus,  label: "Create My Job Profile",  sub: "Get discovered by recruiters",             to: "/post?category=job-profiles" },
        { key: "affiliate",Icon: Tag,       label: "Share an Affiliate Deal", sub: "Earn commissions on partner brands",      to: "/post?mode=affiliate" },
    ];
    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <Button
                    data-testid={`post-ad-header-btn${testIdSuffix}`}
                    aria-label="Post — choose what you want to publish"
                    className={
                        compact
                            ? "aduku-yellow-btn h-10 w-10 rounded-full p-0 hover:bg-aduku-yellow-600 text-[11px] font-bold shadow-sm flex items-center justify-center shrink-0"
                            : "aduku-yellow-btn h-10 rounded-full px-4 lg:px-5 hover:bg-aduku-yellow-600 text-sm font-bold shadow-sm"
                    }
                >
                    <Plus className={compact ? "w-4 h-4" : "w-4 h-4 mr-1"} />
                    <span className={compact ? "hidden" : ""}>Post</span>
                    {!compact && <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-80" />}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={compact ? 12 : 10}
                className="w-72 p-1.5"
                data-testid="post-menu-content"
            >
                <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-aduku-navy/55">
                    What do you want to publish?
                </div>
                {items.map(({ key, Icon, label, sub, to }) => (
                    <DropdownMenuItem
                        key={key}
                        data-testid={`post-menu-${key}`}
                        // v79.68 — DO NOT call `e.preventDefault()` here. Radix
                        // DropdownMenu auto-closes on select ONLY when default
                        // isn't prevented. The previous `e.preventDefault()`
                        // was keeping the menu open until the user tapped
                        // outside — founder-flagged as a recurring UX bug
                        // across every dropdown.
                        onSelect={() => { go(to); }}
                        className="flex items-start gap-2.5 px-2 py-2 rounded-md cursor-pointer focus:bg-aduku-yellow/10"
                    >
                        <span className="mt-0.5 w-7 h-7 rounded-full bg-aduku-yellow/15 text-aduku-yellow-700 flex items-center justify-center shrink-0">
                            <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-semibold text-aduku-navy leading-tight">{label}</span>
                            <span className="block text-[11px] text-aduku-navy/60 leading-tight mt-0.5">{sub}</span>
                        </span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();

    // Sync the search-input state with the URL's ?q= param using React's
    // setState-during-render idiom (the React-19-blessed alternative to
    // setting state inside a useEffect). When the URL search changes, we
    // adopt the new value once; user typing remains independent after.
    const urlQ = React.useMemo(
        () => new URLSearchParams(loc.search).get("q") || "",
        [loc.search],
    );
    const [q, setQ] = React.useState(urlQ);
    const [lastUrlQ, setLastUrlQ] = React.useState(urlQ);
    if (urlQ !== lastUrlQ) {
        setLastUrlQ(urlQ);
        setQ(urlQ);
    }

    const submitSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        navigate(`/browse?${params.toString()}`);
    };

    // v79.138 — Founder direction: "ads search bar is not required when
    // user is viewing funboard". The Ads-search row is context-specific
    // to the marketplace; on the Funboard the user already has an
    // in-page Funboard search icon next to the Refresh button, so the
    // Ads search bar is redundant / confusing. Hide it on /funboard
    // (and any nested Funboard route like /funboard/saved).
    const onFunboard = loc.pathname === "/funboard" || loc.pathname.startsWith("/funboard/");

    // v79.154 — Founder report: "fix the funboard header as this is
    // moving up while i was swiping the feed". Root cause: the global
    // Adukoo header is `sticky top-0 z-[100]` and the Funboard's own
    // header is `sticky top-0 z-30`. When the user scrolls, both try
    // to stick at y=0 — the global one wins (higher z-index + earlier
    // in the DOM) and the Funboard's sticky header disappears
    // underneath it. Fix: on the /funboard route (the top-level feed
    // page), unmount the global header entirely. Funboard already has
    // its own comprehensive top-bar (Back-to-Marketplace pill, refresh,
    // search, view toggle, filter, saved) so nothing is lost. /funboard/
    // sub-pages like /saved still keep the global header for consistency
    // with the rest of the app.
    if (loc.pathname === "/funboard") return null;

    return (
        <header
            data-testid="aduku-header"
            // v78.44 — Header z-index bumped 60 → 100 (per founder spec).
            // Header now sits ABOVE every Radix portal default (Dropdown,
            // Popover, Tooltip = z-50; Dialog / AlertDialog / Sheet =
            // z-50 overlay + z-50 content). DropdownMenuContent is lowered
            // to z-40 in `components/ui/dropdown-menu.jsx` so the dropdown
            // panel slides UNDER the header rather than covering it — this
            // is the structural fix for the founder-reported "header
            // disappears immediately after clicking any element" bug.
            // Intentional full-screen modals (Dialog / AlertDialog overlay)
            // stay above the header because their parent overlay uses the
            // SAME z-50 stacking context as content but spans the whole
            // viewport — the header doesn't reach them visually.
            className="sticky top-0 z-[100] bg-aduku-navy text-white border-b border-aduku-navy-800"
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-6">
                {/* DESKTOP (>=lg, 1024 px+): single nav-bar row — logo |
                    search (flex-1) | utility cluster. Tablet (768-1023)
                    intentionally stays on the mobile 2-row layout so the
                    search bar isn't squeezed to 190 px on iPads.

                    MOBILE / TABLET (<xl): two rows — row 1 logo + Post +
                    Login; row 2 full-width search bar.
                    v79.235 — Bumped the desktop/mobile switch from `lg`
                    (1024 px) → `xl` (1280 px). Founder report on Motorola
                    Edge 60: the WebView briefly reports viewport ≥ 1024
                    (curved-display miscount) and the desktop layout kicked
                    in on a phone-sized screen, showing a huge truncated
                    "Ad…" brand and wrap-broken nav. `xl` gives us a 256 px
                    safety margin so any Android WebView that miscounts by
                    a few hundred pixels still stays on the mobile layout. */}
                <div className="hidden xl:block py-3 space-y-3">
                    {/* Row 1 — logo + utility nav cluster (right-aligned).
                        v77.37 — moved the search bar onto its own dedicated
                        Row 2 below so it stays a comfortable full-width
                        target on EVERY viewport (laptop / iPad / 10″ tab /
                        Android web-view) — the founder reported the
                        single-row layout squeezed search to ~190 px once
                        the My-Stores + Digital-Stores buttons landed. */}
                    <div className="flex items-center gap-3 xl:gap-4 h-14">
                        <Link
                            to="/"
                            data-testid="brand-logo"
                            data-protected="true"
                            translate="no"
                            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                            className="notranslate flex items-center shrink-0 select-none"
                            aria-label="Adukoo — Worldwide marketplace."
                        >
                            {/* v79.47 — Founder direction: header must be a
                                single tidy line (no stacked tagline above
                                search). The "Worldwide marketplace." subtitle
                                that sat below the Adukoo wordmark added a
                                second row of text on ≥ lg screens, breaking
                                the clean 1-row alignment with the nav pills.
                                Removed it here — the tagline is still present
                                in the Home hero copy and page metadata for
                                SEO / branding purposes. */}
                            <div
                                className="font-display font-black text-3xl xl:text-4xl tracking-tight text-aduku-yellow leading-none"
                                style={{
                                    // v79.234 — Founder report: Adukoo wordmark
                                    // renders in dark navy (invisible) on
                                    // Motorola Edge 60 Chrome for Android even
                                    // though every other device shows it in
                                    // yellow. Root cause: Chrome for Android's
                                    // system-wide "Force Dark" accessibility
                                    // setting overrides `<meta color-scheme>`.
                                    // Belt-and-braces:
                                    //  1. `color-scheme: only light` opts THIS
                                    //     element out of any UA dark scheme
                                    //     regardless of system setting.
                                    //  2. `-webkit-text-fill-color` is a legacy
                                    //     WebKit property that Chrome's force-
                                    //     dark filter deliberately leaves
                                    //     alone — perfect for pinning a brand
                                    //     colour that must never invert.
                                    //  3. `isolation: isolate` prevents ancestor
                                    //     blend/filter modes from bleeding in.
                                    color: "#F5C300",
                                    WebkitTextFillColor: "#F5C300",
                                    colorScheme: "only light",
                                    isolation: "isolate",
                                }}
                            >
                                Adukoo<sup className="text-xs xl:text-sm align-super ml-0.5" style={{ color: "#F5C300", WebkitTextFillColor: "#F5C300" }}>™</sup>
                            </div>
                        </Link>

                        <div className="flex items-center gap-1.5 xl:gap-2 shrink-0 ml-auto [&_a]:whitespace-nowrap [&_button]:whitespace-nowrap">
                            <LanguagePicker />
                            {/* v79.68 — Dedicated Home pill on desktop nav. */}
                            <Link
                                to="/"
                                data-testid="home-header-link"
                                aria-label="Home"
                                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                                className="inline-flex h-10 px-3 lg:px-3.5 rounded-full items-center gap-1.5 bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-sm font-semibold transition-colors"
                            >
                                <Home className="w-4 h-4 opacity-90" />
                                <span className="hidden lg:inline">Home</span>
                            </Link>
                            <Link
                                to="/how-it-works"
                                data-testid="how-it-works-header-link"
                                className="funboard-glow hidden xl:inline-flex h-10 px-3.5 rounded-full items-center gap-1.5 bg-aduku-yellow text-aduku-navy text-sm font-black transition-transform hover:scale-105 shadow-sm"
                            >
                                <HelpCircle className="w-4 h-4" /> How it works
                            </Link>
                            {/* v78.65 — Public B2B hub. Renamed from
                                "Catalogs" → "For Businesses" per founder.
                                Lives in the desktop nav row (≥1024 px).
                                For phones/tablets the mobile secondary
                                row below the search bar handles it. */}
                            <a
                                href="/marketing-assets/catalogs/index.html"
                                data-testid="for-businesses-header-link"
                                className="funboard-glow hidden lg:inline-flex h-10 px-3.5 rounded-full items-center gap-1.5 bg-aduku-yellow text-aduku-navy text-sm font-black transition-transform hover:scale-105 shadow-sm"
                            >
                                <FileText className="w-4 h-4" /> For Businesses
                            </a>
                            <Link
                                to="/digital-stores"
                                data-testid="stores-header-link"
                                className="inline-flex h-10 px-3 lg:px-3.5 rounded-full items-center gap-1.5 bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-sm font-semibold transition-colors"
                            >
                                <Store className="w-4 h-4 opacity-80" />
                                <span className="hidden xl:inline">Digital Stores</span>
                                <span className="xl:hidden">Stores</span>
                            </Link>
                            {/* v79.98 — Funboard tab. Yellow pill so it stands
                                out from the Marketplace surfaces; single-tap
                                switches the whole app into swipe-feed mode.
                                The Funboard page itself has a "back to
                                Marketplace" pill so users always have a
                                one-tap return.
                                v79.229 — Founder direction: users didn't
                                recognise the icon-only pill on mobile.
                                Show full "Funboard" label on ALL devices
                                and drop the NEW tag entirely. */}
                            <Link
                                to="/funboard"
                                data-testid="funboard-header-link"
                                className="funboard-glow relative inline-flex h-10 px-3 lg:px-3.5 rounded-full items-center gap-1.5 bg-aduku-yellow text-aduku-navy text-sm font-black transition-transform hover:scale-105 shadow-sm whitespace-nowrap"
                                title="Funboard by Adukoo — memes, videos & marketplace picks"
                            >
                                <span aria-hidden="true">🎉</span>
                                <span>Funboard</span>
                            </Link>
                            {user && (
                                <Link
                                    to="/businesses/mine"
                                    data-testid="my-stores-header-link"
                                    className="inline-flex h-10 px-3 lg:px-3.5 rounded-full items-center gap-1.5 bg-aduku-yellow/10 hover:bg-aduku-yellow/20 text-aduku-yellow text-sm font-semibold transition-colors border border-aduku-yellow/30"
                                    title="View and manage all your Digital Stores"
                                >
                                    <Store className="w-4 h-4" />
                                    <span className="hidden xl:inline">My Stores</span>
                                    <span className="xl:hidden">Mine</span>
                                </Link>
                            )}
                            <PostMenu />
                            {user && <NotificationsBell sideOffset={88} />}
                            {user ? (
                                <DropdownMenu modal={false}>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            data-testid="user-menu-trigger"
                                            aria-label={user.name ? `${user.name} account menu` : "Account menu"}
                                            className="h-10 w-10 rounded-full bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white flex items-center justify-center transition-colors shrink-0"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-aduku-yellow text-aduku-navy flex items-center justify-center text-xs font-bold overflow-hidden">
                                                {user.picture ? (
                                                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    user.name?.[0]?.toUpperCase() || "U"
                                                )}
                                            </div>
                                        </button>
                                    </DropdownMenuTrigger>
                                    {/* v78.48 — sideOffset pushes the dropdown panel
                                        BELOW the bottom edge of the sticky header instead
                                        of just below the trigger button. Founder report:
                                        "the header completely disappears when I click on
                                        the account tab or the notification button. It
                                        reappears again if I tap anywhere on the website."
                                        Root cause: the dropdown trigger lives INSIDE the
                                        header, and the dropdown panel at z-[110] (set in
                                        v78.46 so dropdown items aren't hidden behind the
                                        header) opened just below the trigger, which left
                                        the panel visually covering ~50-90 px of the lower
                                        header (search bar + half the action row). With
                                        sideOffset={88} the panel slides cleanly below the
                                        desktop header (~141 px). The mobile dropdown uses
                                        sideOffset={56} because the mobile header (~156 px)
                                        has the trigger lower in the layout. */}
                                    <DropdownMenuContent align="end" sideOffset={88} className="w-64">
                                        {/* v79.97 — Signed-in identity header.
                                            Founder feedback: users with multiple
                                            Adukoo accounts couldn't tell which
                                            email they were currently signed in
                                            with without navigating to Profile.
                                            Show it upfront (non-clickable). */}
                                        <div className="px-2 py-2 border-b border-aduku-border/60" data-testid="account-identity">
                                            <div className="text-[10px] uppercase tracking-wider font-bold text-aduku-navy/50">Signed in as</div>
                                            <div className="text-sm font-semibold text-aduku-navy truncate" title={user.name || ""}>{user.name || "—"}</div>
                                            <div className="text-[11px] text-aduku-navy/65 truncate" title={user.email || ""} data-testid="account-identity-email">{user.email || ""}</div>
                                        </div>
                                        {/* v78.44 — Switched from inline `onClick={() => navigate(...)}`
                                            to Radix's `onSelect` callback, AND deferred the actual
                                            `navigate(...)` call by one tick via `setTimeout(.., 0)`.
                                            Founder report: clicking any of these items caused the
                                            entire page to freeze and the header to vanish.
                                            Root cause: `onClick` ran SYNCHRONOUSLY during the same
                                            React commit as Radix's portal-close transition, so React
                                            tried to unmount the open dropdown's portal AND mount the
                                            new route page in the same tick. The portal-close
                                            animation (zoom-out + fade-out) raced with the new route's
                                            initial mount and Radix's focus-trap left the body in
                                            `inert` state, freezing every click target including the
                                            sticky header. Using `onSelect` lets Radix fully tear down
                                            the portal first (`onSelect` fires BEFORE the close
                                            animation but the menu is already marked closed); the
                                            `setTimeout(0)` then schedules the navigate for the next
                                            macrotask, after the portal unmount has flushed. */}
                                        <DropdownMenuItem data-testid="menu-my-listings" onSelect={() => setTimeout(() => navigate("/my-listings"), 0)}>
                                            <User className="w-4 h-4 mr-2" /> My Listings
                                        </DropdownMenuItem>
                                        <DropdownMenuItem data-testid="menu-analytics" onSelect={() => setTimeout(() => navigate("/analytics"), 0)}>
                                            <BarChart3 className="w-4 h-4 mr-2 text-aduku-yellow-600" /> 360° Analytics
                                            {!(user.verified || user.business_verified) && user.analytics_access !== true && (
                                                <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-aduku-navy/50">
                                                    <Lock className="w-2.5 h-2.5" /> Verified
                                                </span>
                                            )}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem data-testid="menu-messages" onSelect={() => setTimeout(() => navigate("/messages"), 0)}>
                                            <MessageSquare className="w-4 h-4 mr-2" /> Messages
                                        </DropdownMenuItem>
                                        <DropdownMenuItem data-testid="menu-alerts" onSelect={() => setTimeout(() => navigate("/alerts"), 0)}>
                                            <Bell className="w-4 h-4 mr-2" /> Alerts
                                        </DropdownMenuItem>
                                        <DropdownMenuItem data-testid="menu-wishlist" onSelect={() => setTimeout(() => navigate("/wishlist"), 0)}>
                                            <Heart className="w-4 h-4 mr-2" /> Wishlist
                                        </DropdownMenuItem>
                                        {/* v79.180 — Item 21: My Funboard moved under
                                            Marketplace Account so all personal data
                                            lives in one dropdown. */}
                                        <DropdownMenuItem data-testid="menu-my-funboard" onSelect={() => setTimeout(() => navigate("/funboard/saved"), 0)}>
                                            <Bookmark className="w-4 h-4 mr-2 text-aduku-yellow-600" /> My Funboard
                                        </DropdownMenuItem>
                                        {/* v79.181 — Community Forum entry point */}
                                        <DropdownMenuItem data-testid="menu-forum" onSelect={() => setTimeout(() => navigate("/forum"), 0)}>
                                            <MessageSquare className="w-4 h-4 mr-2" /> Community Forum
                                        </DropdownMenuItem>
                                        {(user.employer_verified || user.role === "admin") && (
                                            <DropdownMenuItem data-testid="menu-employer-shortlist" onSelect={() => setTimeout(() => navigate("/employer/shortlist"), 0)}>
                                                <BookmarkCheck className="w-4 h-4 mr-2 text-aduku-yellow-600" /> Saved Candidates
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem data-testid="menu-profile" onSelect={() => setTimeout(() => navigate("/profile"), 0)}>
                                            <User className="w-4 h-4 mr-2" /> Edit Profile
                                        </DropdownMenuItem>
                                        <DropdownMenuItem data-testid="menu-my-shop" onSelect={() => setTimeout(() => navigate("/businesses/mine"), 0)}>
                                            <Store className="w-4 h-4 mr-2 text-aduku-navy" />
                                            My Digital Stores
                                        </DropdownMenuItem>
                                        {user.role === "admin" && (
                                            <DropdownMenuItem data-testid="menu-clients" onSelect={() => setTimeout(() => navigate("/profile/clients"), 0)}>
                                                <BookmarkCheck className="w-4 h-4 mr-2 text-aduku-navy" />
                                                Manage Clients
                                            </DropdownMenuItem>
                                        )}
                                        {/* v78.46 — Trimmed three rarely-used / reachable-elsewhere
                                            items from the dropdown (founder report: My Listings +
                                            360° Analytics were getting CLIPPED off the top because
                                            the dropdown was too tall to fit the viewport on a 1280×900
                                            screen):
                                              - "+ New Digital Store" → primary CTA inside the
                                                My Digital Stores page itself, no need to duplicate
                                              - "What's a Digital Store?" → linked from My Digital
                                                Stores page header
                                              - "How Adukoo works (full guide)" → already in the
                                                top-nav as "How it works"
                                            Saves three rows so My Listings + 360° Analytics fit on
                                            screen for every account type without scrolling. */}
                                        {user.account_type === "business" && !(user.verified || user.business_verified) && (
                                            <DropdownMenuItem data-testid="menu-get-verified" onSelect={() => setTimeout(() => navigate("/account/verify?type=business"), 0)}>
                                                <Shield className="w-4 h-4 mr-2 text-aduku-yellow-600" /> Get Verified Badge
                                            </DropdownMenuItem>
                                        )}
                                        {user.role === "admin" && (
                                            <DropdownMenuItem data-testid="menu-admin" onSelect={() => setTimeout(() => navigate("/admin"), 0)}>
                                                <Shield className="w-4 h-4 mr-2" /> Admin Panel
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem data-testid="menu-logout" onSelect={() => setTimeout(() => logout(), 0)}>
                                            <LogOut className="w-4 h-4 mr-2" /> Logout
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Link to="/login" data-testid="login-header-btn">
                                    <Button variant="ghost" className="text-white/90 hover:text-white bg-aduku-navy-800/70 hover:bg-aduku-navy-700 h-10 rounded-full px-4 text-sm font-semibold transition-colors">
                                        <User className="w-4 h-4 mr-1.5 opacity-80" /> Login
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>

                    {/* Row 2 — full-width search bar (desktop). Same UX
                        pattern the mobile/tablet layout has used since v33.
                        v79.138 — hidden on /funboard (Ads search is not
                        relevant there). */}
                    {!onFunboard && (
                    <form onSubmit={submitSearch} className="w-full">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-aduku-navy/60 pointer-events-none" />
                            <input
                                data-testid="header-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search Ads, brands, categories, stores..."
                                className="w-full h-12 pl-11 pr-32 rounded-full bg-white text-aduku-navy placeholder-aduku-navy/50 focus:outline-none focus:ring-2 focus:ring-aduku-yellow text-base"
                            />
                            <button
                                data-testid="header-search-submit"
                                type="submit"
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 lg:h-10 px-5 lg:px-6 rounded-full aduku-yellow-btn text-sm font-bold"
                            >
                                Search
                            </button>
                        </div>
                    </form>
                    )}
                </div>

                {/* MOBILE + TABLET (<lg, up to 1023 px) — compact 2-row
                    header that fits cleanly on everything from a 360 px
                    Android phone up to an iPad (1024 px). Row 1: brand
                    logo + utility actions. Row 2: full-width search +
                    compact Stores pill. Total height ≤ 116 px so content
                    sits comfortably above the fold on a phone.
                    v79.235 — `xl:hidden` (was `lg:hidden`) so any phone /
                    small tablet up to 1279 px uses this compact layout,
                    including Moto Edge 60 whose WebView occasionally
                    reports a > 1024 px viewport on its curved display. */}
                <div className="xl:hidden py-2.5">
                    {/* Row 1 (v79.25 — MERGED): Compact "Adukoo" brand
                        pill + action cluster on the SAME row so the
                        header stops visually duplicating the hero on
                        the Home page. Founder report: "White colour
                        is sorted but new problem started as it became
                        duplication where I see Adukoo twice ... sticky
                        header and the below screen is moving below that
                        blue header". Root cause: the earlier layout had
                        a full-width standalone "Adukoo™ Worldwide
                        marketplace." wordmark row that consumed ~52 px
                        above the action buttons, and directly under it
                        the Home hero's H1 ("Adukoo is for everyone.")
                        made the brand feel repeated. Merging them into
                        a single row keeps the brand visible on every
                        page (still tappable → routes to `/`) but
                        removes the perceived duplication and gives
                        ~50 px of vertical space back to above-the-fold
                        content. The tagline "Worldwide marketplace."
                        remains prominent inside the Home hero on
                        desktop and in the H1 copy on mobile. */}
                    {/* v79.40 — Fixed landscape overflow. Founder report:
                        "when user rotates mobile the website view and
                        content should be very clean". At 844×390
                        (landscape mobile) the previous `sm:justify-end`
                        combined with `mr-auto` on the brand caused the
                        row items to exceed the container width, which
                        pushed the Adukoo brand ~27 px OFF-SCREEN LEFT.
                        Root cause: 7 items (Adukoo + Language + How
                        it works + For Businesses + Stores + Post Ad
                        + Login) totalled ~858 px vs a ~804 px
                        container. With `mr-auto` on the first item
                        AND `justify-end` on the parent, that overflow
                        collapsed into a negative left position.

                        Fix: use `justify-between` at every mobile
                        width so items always spread evenly across the
                        available row. Drop `mr-auto` from the brand
                        (no longer needed — `justify-between` puts the
                        brand at the left and the last button at the
                        right automatically). Also tighten the tablet
                        gap so the row breathes without overflowing. */}
                    {/* v79.43 — Row 1 forced to a SINGLE row (no wrap).
                        Founder report: "login and post ad buttons also
                        should be in same line. Seems you have messed up."
                        Root cause: v79.42 used `flex-wrap` and gave "For
                        Businesses" a yellow-highlighted pill wider than
                        the other icon buttons, so at 390-430 px mobile
                        the row overflowed and Post Ad + user icon
                        wrapped to a second line. Fix: `flex-nowrap`, gap
                        reduced to `gap-1`, and Post Ad tightened to
                        icon-only on `<sm` so all 7 items (Adukoo brand +
                        globe + store + help + for-biz + Post Ad + user)
                        squeeze into ONE line at 390 px. All items also
                        marked `min-w-0` so they can shrink if needed. */}
                    <div className="flex items-center justify-between gap-1 sm:gap-2 mb-2.5 min-w-0 flex-nowrap">
                        <Link
                            to="/"
                            data-testid="brand-logo-mobile"
                            data-protected="true"
                            translate="no"
                            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                            className="notranslate flex items-center select-none shrink-0"
                            aria-label="Adukoo — Worldwide marketplace."
                        >
                            <div
                                className="font-display font-black text-xl sm:text-2xl tracking-tight text-aduku-yellow leading-none"
                                style={{
                                    // v79.234 — Chrome for Android "Force Dark"
                                    // was rendering the mobile brand wordmark
                                    // in dark navy on Motorola Edge 60 (and
                                    // similar). See the desktop-brand comment
                                    // above for the full three-line defense.
                                    color: "#F5C300",
                                    WebkitTextFillColor: "#F5C300",
                                    colorScheme: "only light",
                                    isolation: "isolate",
                                }}
                            >
                                Adukoo<sup className="text-[9px] sm:text-[10px] align-super ml-0.5" style={{ color: "#F5C300", WebkitTextFillColor: "#F5C300" }}>™</sup>
                            </div>
                        </Link>
                        {/* v79.68 — Founder direction: "The Android application
                            currently lacks a direct home button." The brand
                            logo already navigates to / but users don't always
                            realise it. Dedicated Home pill placed right after
                            the brand as the FIRST tappable icon on the mobile
                            row — same size and style as the other icon-pills
                            so nothing gets pushed out of alignment.
                            v79.231 — HIDDEN entirely on <sm. Founder report:
                            "in mobile browser account section vanished".
                            Once the Funboard pill was widened to show its
                            label the row overflowed at 390 px portrait and
                            pushed the Account trigger off-screen. The brand
                            logo already links to `/`, so this icon was pure
                            duplication and the safest thing to trim. Kept
                            visible at ≥sm where the row has plenty of room. */}
                        <Link
                            to="/"
                            data-testid="home-header-link-mobile"
                            aria-label="Home"
                            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                            className="hidden sm:inline-flex h-10 w-10 rounded-full items-center justify-center bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-[11px] font-semibold transition-colors shrink-0"
                        >
                            <Home className="w-4 h-4 opacity-90" />
                        </Link>
                        <div className="shrink-0 min-w-0">
                            <LanguagePicker />
                        </div>
                        <Link
                            to="/digital-stores"
                            data-testid="stores-header-link-tablet"
                            aria-label="Digital Stores"
                            className="hidden sm:inline-flex h-10 w-10 rounded-full items-center justify-center bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-[11px] font-semibold transition-colors shrink-0"
                        >
                            <Store className="w-4 h-4 opacity-80" />
                        </Link>
                        {/* v79.100 — Funboard pill visible on mobile (was
                            desktop-only in v79.98). Yellow so it stands
                            out against the navy header.
                            v79.229 — Founder direction: users couldn't
                            identify the icon-only pill. Show the full
                            "Funboard" label on mobile too and drop the
                            NEW dot. Compact padding (px-3, h-9) keeps it
                            inside the mobile row without pushing the
                            Account trigger off-screen. */}
                        <Link
                            to="/funboard"
                            data-testid="funboard-header-link-mobile"
                            aria-label="Funboard"
                            title="Funboard by Adukoo"
                            className="funboard-glow relative inline-flex h-9 px-3 rounded-full items-center gap-1 bg-aduku-yellow text-aduku-navy text-[13px] font-black transition-transform hover:scale-105 shadow-sm shrink-0 whitespace-nowrap"
                        >
                            <span aria-hidden="true">🎉</span>
                            <span>Funboard</span>
                        </Link>
                        {/* v79.42 — Compact "How it works" pill promoted from
                            Row 3 into Row 1. Icon-only on <sm to preserve
                            one-line layout at 390 px portrait; icon+label
                            at sm+ where there's room.
                            v79.78 — HIDDEN entirely on <sm. Founder report:
                            "account section vanished completely on mobile
                            browser". Root cause: 8 icon pills + brand logo
                            were overflowing a 390 px row and pushing the
                            Account/Login trigger off-screen. This entry
                            still lives inside the mobile user dropdown as
                            "How Adukoo works" (menu-how-it-works-mobile). */}
                        <Link
                            to="/how-it-works"
                            data-testid="how-it-works-header-link-inline"
                            aria-label="How it works"
                            className="hidden sm:inline-flex h-10 w-10 rounded-full items-center justify-center bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-[11px] font-semibold transition-colors shrink-0"
                        >
                            <HelpCircle className="w-4 h-4 opacity-80" />
                        </Link>
                        {/* v79.42 — "For Businesses" pill. Icon-only on <sm,
                            icon+label on sm+. v79.43 — removed the yellow
                            border/bg highlight that was making it wider
                            than the other pills and pushing Post Ad + user
                            to a wrapped second line at ~430 px mobile.
                            Style now matches Stores + How-it-works so all
                            four icon pills are equal width.
                            v79.78 — HIDDEN on <sm for the same reason as
                            How-it-works above. B2B users can still reach
                            the catalog from the footer + the "For
                            Businesses" nav link inside the Post-Ad menu. */}
                        <a
                            href="/marketing-assets/catalogs/index.html"
                            data-testid="for-businesses-header-link-inline"
                            aria-label="For Businesses"
                            className="hidden sm:inline-flex h-10 w-10 rounded-full items-center justify-center bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-[11px] font-semibold transition-colors shrink-0"
                        >
                            <FileText className="w-4 h-4 opacity-80" />
                        </a>
                        <PostMenu compact testIdSuffix="-mobile" />
                        {user && <NotificationsBell sideOffset={56} />}
                        {user ? (
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="user-menu-trigger-mobile"
                                        aria-label="Account menu"
                                        className="h-10 w-10 shrink-0 rounded-full bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white flex items-center justify-center transition-colors"
                                    >
                                        <div className="w-7 h-7 rounded-full bg-aduku-yellow text-aduku-navy flex items-center justify-center text-xs font-bold overflow-hidden">
                                            {user.picture ? (
                                                <img src={user.picture} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                user.name?.[0]?.toUpperCase() || "U"
                                            )}
                                        </div>
                                    </button>
                                </DropdownMenuTrigger>
                                {/* v78.48 — sideOffset for mobile dropdown. Trigger sits
                                    in the second row of the 156-px-tall mobile header at
                                    ~y=68 (~40 px tall), so the gap to header-bottom is
                                    ~48 px; sideOffset={56} clears it with ~8 px breathing
                                    room. See desktop dropdown above for full rationale. */}
                                <DropdownMenuContent align="end" sideOffset={56} className="w-64">
                                    {/* v79.97 — Signed-in identity (mobile).
                                        Mirrors the desktop dropdown so users
                                        with multiple Adukoo accounts always
                                        see which email they're on. */}
                                    <div className="px-2 py-2 border-b border-aduku-border/60" data-testid="account-identity-mobile">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-aduku-navy/50">Signed in as</div>
                                        <div className="text-sm font-semibold text-aduku-navy truncate" title={user.name || ""}>{user.name || "—"}</div>
                                        <div className="text-[11px] text-aduku-navy/65 truncate" title={user.email || ""} data-testid="account-identity-email-mobile">{user.email || ""}</div>
                                    </div>
                                    {/* v78.44 — Mirror the desktop dropdown's onSelect +
                                        setTimeout(navigate,0) pattern here for defense-in-depth.
                                        The iOS Safari freeze the founder reported was the same
                                        portal-close-vs-route-mount race; while it didn't always
                                        reproduce on mobile in automation, keeping desktop and
                                        mobile dropdowns symmetrical avoids the regression. */}
                                    <DropdownMenuItem data-testid="menu-my-listings-mobile" onSelect={() => setTimeout(() => navigate("/my-listings"), 0)}>
                                        <User className="w-4 h-4 mr-2" /> My Listings
                                    </DropdownMenuItem>
                                    {/* v78.47 — 360° Analytics was missing from the mobile
                                        dropdown (desktop had it but the mobile branch was
                                        never given the matching entry). Founder report:
                                        "the 360 analytics option is not displaying for
                                        users who are using the Android app or mobile
                                        browsers". Adding it here so both surfaces stay in
                                        sync; the same Verified-lock pill renders for users
                                        who don't yet have analytics access. */}
                                    <DropdownMenuItem data-testid="menu-analytics-mobile" onSelect={() => setTimeout(() => navigate("/analytics"), 0)}>
                                        <BarChart3 className="w-4 h-4 mr-2 text-aduku-yellow-600" /> 360° Analytics
                                        {!(user.verified || user.business_verified) && user.analytics_access !== true && (
                                            <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-aduku-navy/50">
                                                <Lock className="w-2.5 h-2.5" /> Verified
                                            </span>
                                        )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="menu-messages-mobile" onSelect={() => setTimeout(() => navigate("/messages"), 0)}>
                                        <MessageSquare className="w-4 h-4 mr-2" /> Messages
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="menu-alerts-mobile" onSelect={() => setTimeout(() => navigate("/alerts"), 0)}>
                                        <Bell className="w-4 h-4 mr-2" /> Alerts
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="menu-wishlist-mobile" onSelect={() => setTimeout(() => navigate("/wishlist"), 0)}>
                                        <Heart className="w-4 h-4 mr-2" /> Wishlist
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="menu-profile-mobile" onSelect={() => setTimeout(() => navigate("/profile"), 0)}>
                                        <User className="w-4 h-4 mr-2" /> Edit Profile
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="menu-my-shop-mobile" onSelect={() => setTimeout(() => navigate(user.account_type === "business" ? "/businesses/mine" : "/shops/me/edit"), 0)}>
                                        <Store className="w-4 h-4 mr-2 text-aduku-navy" />
                                        {user.account_type === "business" ? "My Digital Stores" : "Open a Digital Store"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="menu-how-it-works-mobile" onSelect={() => setTimeout(() => navigate("/how-it-works"), 0)}>
                                        <HelpCircle className="w-4 h-4 mr-2 text-aduku-yellow-600" /> How Adukoo works
                                    </DropdownMenuItem>
                                    {user.role === "admin" && (
                                        <DropdownMenuItem data-testid="menu-admin-mobile" onSelect={() => setTimeout(() => navigate("/admin"), 0)}>
                                            <Shield className="w-4 h-4 mr-2" /> Admin Panel
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem data-testid="menu-logout-mobile" onSelect={() => setTimeout(() => logout(), 0)}>
                                        <LogOut className="w-4 h-4 mr-2" /> Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/login" data-testid="login-header-btn-mobile" aria-label="Login" className="shrink-0">
                                <Button
                                    variant="ghost"
                                    className="text-white/90 hover:text-white bg-aduku-navy-800/70 hover:bg-aduku-navy-700 h-10 w-10 p-0 rounded-full flex items-center justify-center transition-colors"
                                >
                                    <User className="w-4 h-4" />
                                </Button>
                            </Link>
                        )}
                    </div>
                    {/* Row 3: full-width search bar.
                        v79.138 — hidden on /funboard (Ads search is
                        irrelevant there; Funboard has its own inline
                        search icon in the toolbar). */}
                    {!onFunboard && (
                    <div className="flex items-center gap-2">
                        <form onSubmit={submitSearch} className="flex-1 min-w-0">
                            <div className="relative">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-aduku-navy/60 pointer-events-none" />
                                <input
                                    data-testid="header-search-input-mobile"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search Ads, brands, categories..."
                                    className="w-full h-11 pl-10 pr-24 rounded-full bg-white text-aduku-navy placeholder-aduku-navy/50 focus:outline-none focus:ring-2 focus:ring-aduku-yellow text-sm"
                                />
                                <button
                                    data-testid="header-search-submit-mobile"
                                    type="submit"
                                    className="absolute right-1 top-1 h-9 px-4 rounded-full aduku-yellow-btn text-xs font-bold"
                                >
                                    Search
                                </button>
                            </div>
                        </form>
                    </div>
                    )}
                    {/* v78.65 — Row 4 (mobile / tablet < lg): secondary
                        nav with "How it works" + "For Businesses" so
                        every device < 1024 px (phones AND iPad-portrait
                        tablets) has a one-tap B2B catalog entry. Hidden
                        on lg+ because those buttons sit in the desktop
                        nav row above. Breakpoint matches the parent
                        mobile section's `lg:hidden` so there is no
                        dead zone between 768 px and 1024 px.
                        v79.4 — REMOVED the standalone "Get the app"
                        Play Store pill from this row. On narrow Android
                        viewports it wrapped to its own line and looked
                        orphaned (founder screenshot). The Play Store
                        badge is already prominently surfaced in the
                        Home hero (full PNG badge with "Now Live"
                        callout) and the Footer (PNG badge + Share),
                        so the header doesn't need a third install path. */}
                    {/* v79.42 — Row 3 removed. "How it works" + "For
                        Businesses" pills moved into Row 1 above the
                        search box (see the `-inline` variants). Hidden
                        everywhere to keep the mobile header compact. */}
                    <div className="hidden">
                        <Link
                            to="/how-it-works"
                            data-testid="how-it-works-header-link-mobile"
                            className="inline-flex h-8 px-3 rounded-full items-center gap-1.5 bg-aduku-navy-800/60 hover:bg-aduku-navy-700 text-white/90 hover:text-white text-[11px] sm:text-xs font-semibold transition-colors"
                        >
                            <HelpCircle className="w-3.5 h-3.5 opacity-80" /> How it works
                        </Link>
                        <a
                            href="/marketing-assets/catalogs/index.html"
                            data-testid="for-businesses-header-link-mobile"
                            className="inline-flex h-8 px-3 rounded-full items-center gap-1.5 bg-aduku-yellow/15 hover:bg-aduku-yellow/25 text-aduku-yellow border border-aduku-yellow/40 text-[11px] sm:text-xs font-semibold transition-colors"
                        >
                            <FileText className="w-3.5 h-3.5 opacity-80" /> For Businesses
                        </a>
                    </div>
                </div>
            </div>
        </header>
    );
}
